import backoff from 'backoff';
import { v4 as uuidv4 } from 'uuid';

import { safeJsonParse } from '~/utils/json';

const PING_CMD = '1';
const PONG_CMD = '2';

export enum WSS_ACTIONS {
  // Service actions
  SERVER_ERROR = 'SERVER_ERROR',
  CWARS_GET_STAGES_INFO = 'CWARS_GET_STAGES_INFO',
  CWARS_GET_ALLIES_ATTEMPTS = 'CWARS_GET_ALLIES_ATTEMPTS',
  CWARS_GET_ENEMY_GRADES = 'CWARS_GET_ENEMY_GRADES',
  CWARS_GET_SETTINGS = 'CWARS_GET_SETTINGS',
  CWARS_GET_COMMON_INFO = 'CWARS_GET_COMMON_INFO',
  CWARS_GET_LAST_ROUND_RESULTS = 'CWARS_GET_LAST_ROUND_RESULTS',
  CWARS_SET_ACCOUNT_SETTINGS = 'CWARS_SET_ACCOUNT_SETTINGS',
  CWARS_SET_CLAN_SETTINGS = 'CWARS_SET_CLAN_SETTINGS',
  CWARS_GET_RATINGS = 'CWARS_GET_RATINGS',
  CWARS_RATINGS_SEARCH = 'CWARS_RATINGS_SEARCH',
  CWARS_RATINGS_SEARCH_AUTOCOMPLETE = 'CWARS_RATINGS_SEARCH_AUTOCOMPLETE',
  CWARS_GET_ROUNDS_RESULTS_HISTORY = 'CWARS_GET_ROUNDS_RESULTS_HISTORY',
  // Store actions
  CWARS_UPDATE_ALLIES_ATTEMPTS = 'CWARS_UPDATE_ALLIES_ATTEMPTS',
  CWARS_UPDATE_COMMON_INFO = 'CWARS_UPDATE_COMMON_INFO',
  CWARS_UPDATE_ENEMY_GRADES = 'CWARS_UPDATE_ENEMY_GRADES',
  CWARS_UPDATE_LAST_ROUND_RESULTS = 'CWARS_UPDATE_LAST_ROUND_RESULTS',
  CWARS_UPDATE_RATINGS = 'CWARS_UPDATE_RATINGS',
  CWARS_UPDATE_RATINGS_SEARCH = 'CWARS_UPDATE_RATINGS_SEARCH',
  CWARS_UPDATE_RATINGS_SEARCH_AUTOCOMPLETE = 'CWARS_UPDATE_RATINGS_SEARCH_AUTOCOMPLETE',
  CWARS_UPDATE_ROUNDS_RESULTS_HISTORY = 'CWARS_UPDATE_ROUNDS_RESULTS_HISTORY',
  CWARS_UPDATE_SETTINGS = 'CWARS_UPDATE_SETTINGS',
  CWARS_STAGE_START = 'CWARS_STAGE_START',
  CWARS_UPDATE_ADDITIONAL_ATTEMPTS = 'CWARS_UPDATE_ADDITIONAL_ATTEMPTS',
  CWARS_UPDATE_STAGES = 'CWARS_UPDATE_STAGES',
}

type ISocketProps = {
  failAfter: number;
};

type ISocketRequest<T = Record<string, unknown>> = {
  actionType: WSS_ACTIONS;
  payload: T;
};

export type ISocketResponse<T> = ISocketRequest<T> & {
  requestId: string;
};

export type ISocketError = {
  err: ISocketRequest;
  request: ISocketRequest;
};

class WssService {
  private socket?: WebSocket;
  private url: Nullable<string> = null;
  private isInitialized: boolean = false;
  private requestsQueue: Array<ISocketRequest> = [];

  private exponentialBackoff = backoff.exponential({
    initialDelay: 100,
    maxDelay: 10000,
  });

  public requestsHistory: {
    [requestId: string]: ISocketRequest<Record<string, unknown>>;
  } = {};

  public onOpen?: (e: Event) => void;
  public onMessage?: (e: MessageEvent) => void;
  public onMessageError?: (err: ISocketError) => void;
  public onClose?: (e: CloseEvent) => void;
  public onError?: (e: Event) => void;
  public onFailConnection?: () => void;

  constructor() {
    this.setReconnect({
      failAfter: 10,
    });
  }

  private setReconnect = (props: ISocketProps) => {
    this.exponentialBackoff.failAfter(props.failAfter || 20);

    this.exponentialBackoff.on('ready', () => {
      if (this.url) {
        this.init({
          url: this.url,
        });
      }
    });

    this.exponentialBackoff.on('fail', () => {
      this.onFailConnection?.();
    });
  };

  public init = (props: {
    url: string;
    onMessage?: (e: MessageEvent) => void;
    onOpen?: (e: Event) => void;
    onClose?: (e: CloseEvent) => void;
    onError?: (error: ISocketError) => void;
    onFailConnection?: () => void;
  }) => {
    if (this.isInitialized && props.url === this.url) {
      return;
    }

    if (window.cwarsConnection) {
      window.cwarsConnection.removeEventListener('open', this._onOpen);
      window.cwarsConnection.removeEventListener('message', this._onMessage);
      window.cwarsConnection.removeEventListener('close', this._onClose);
      window.cwarsConnection.removeEventListener('error', this._onError);
      window.cwarsConnection.close();
      delete window.cwarsConnection;
    }

    if (props.url) {
      this.url = props.url;
    }

    this.onMessage = props.onMessage ?? this.onMessage;
    this.onOpen = props.onOpen ?? this.onOpen;
    this.onClose = props.onClose ?? this.onClose;
    this.onMessageError = props.onError ?? this.onMessageError;
    this.onFailConnection = props.onFailConnection ?? this.onFailConnection;

    if (this.url) {
      this.socket = new WebSocket(this.url);

      this.socket.addEventListener('open', this._onOpen);
      this.socket.addEventListener('message', this._onMessage);
      this.socket.addEventListener('close', this._onClose);
      this.socket.addEventListener('error', this._onError);

      this.isInitialized = true;

      if (typeof window !== 'undefined') {
        window.cwarsConnection = this.socket;
      }
    }
  };

  private _onOpen = (e: Event) => {
    this.exponentialBackoff.reset();

    this.flushQueue();

    this.onOpen?.(e);
  };

  private _onClose = (e: CloseEvent) => {
    this.onClose?.(e);

    this.isInitialized = false;
    this.exponentialBackoff.backoff();
  };

  private _onMessage = (e: MessageEvent<string>) => {
    if (e.data === PING_CMD) {
      this.socket?.send(PONG_CMD);
      return;
    }

    const data = safeJsonParse<ISocketResponse<Record<string, unknown>>>(e.data);

    if (data && data.actionType === WSS_ACTIONS.SERVER_ERROR) {
      this.onMessageError?.({
        err: data,
        request: this.requestsHistory[data.requestId],
      });
    } else {
      this.onMessage?.(e);
    }
  };

  private _onError = (e: Event) => {
    console.error(e);

    this.socket?.close();
  };

  private _sendMessage = <T extends Record<string, unknown>>(msg: ISocketRequest<T>) => {
    if (msg?.actionType && msg?.payload && this.socket?.readyState === WebSocket.OPEN) {
      const reqId = uuidv4();
      this.requestsHistory[reqId] = msg;
      this.socket.send(JSON.stringify({ ...msg, requestId: reqId }));
    } else {
      this.requestsQueue.push(msg);
    }
  };

  private flushQueue = () => {
    while (this.requestsQueue.length && this.socket?.readyState === WebSocket.OPEN) {
      const msg = this.requestsQueue.shift()!;
      this._sendMessage(msg);
    }
  };

  public getInitData = () => {
    this.getSettings();
    this.getCommonInfo();
    this.getStagesInfo();
  };

  public getStagesInfo = () => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_GET_STAGES_INFO,
      payload: {},
    });
  };

  public getAlliesAttempts = () => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_GET_ALLIES_ATTEMPTS,
      payload: {},
    });
  };

  public getEnemyGrades = () => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_GET_ENEMY_GRADES,
      payload: {},
    });
  };

  public getSettings = () => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_GET_SETTINGS,
      payload: {},
    });
  };

  public getCommonInfo = () => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_GET_COMMON_INFO,
      payload: {},
    });
  };

  public getLastRoundResults = (clan_id: number) => {
    if (!clan_id) {
      return;
    }

    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_GET_LAST_ROUND_RESULTS,
      payload: { clan_id },
    });
  };

  public toggleAttemtsCount = (isAttemptsCount: boolean) => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_SET_ACCOUNT_SETTINGS,
      payload: { isAttemptsCount },
    });
  };

  public changeJoinRoundAutomatically = (joinRoundAutomatically: boolean) => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_SET_CLAN_SETTINGS,
      payload: { joinRoundAutomatically },
    });
  };

  public changeIsParticipating = (isParticipating: boolean) => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_SET_CLAN_SETTINGS,
      payload: { isParticipating },
    });
  };

  public getRatings = (clanId?: number, league?: number) => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_GET_RATINGS,
      payload: clanId ? { clanId } : { league },
    });
  };

  public getRatingLeaders = () => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_GET_RATINGS,
      payload: {},
    });
  };

  public ratingsSearch = (query: string, offset: number = 0, limit: number = 50) => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_RATINGS_SEARCH,
      payload: { query, limit, offset },
    });
  };

  public ratingsAutocomplete = (query: string) => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_RATINGS_SEARCH_AUTOCOMPLETE,
      payload: { query },
    });
  };

  public getRoundsResultsHistory = (limit: number = 10, offset: number = 0) => {
    this._sendMessage({
      actionType: WSS_ACTIONS.CWARS_GET_ROUNDS_RESULTS_HISTORY,
      payload: { limit, offset },
    });
  };
}

export default new WssService();
